/**
 * ¿Hay una versión nueva del panel publicada? — y cómo pasar a ella sin que quede nada viejo.
 *
 * 🚨 POR QUÉ (el founder, 13-sep-2026): «tengo dropshippers que siguen con la versión vieja, y en
 * Smarty programamos directo a producción». Una pestaña abierta se queda con el bundle que cargó:
 * el service worker nuevo se instala, pero la app en memoria sigue siendo la anterior hasta que
 * alguien recarga — y nadie recarga. AliClick lo resuelve con un aviso que no deja hacer nada más:
 * «Tenemos una nueva versión para ti» + Actualizar. Esto es eso.
 *
 * Cómo se sabe: el deploy escribe `version.json` con el sha del build (`VITE_APP_VERSION`, el mismo
 * que ya usa `cachePersistente`). El panel lo pide sin caché cada tanto y al volver a la pestaña; si
 * la versión publicada no es la que corre, se avisa.
 *
 * 🪤 «ACTUALIZAR» NO ES SÓLO RECARGAR. Un F5 puede volver a servir lo viejo desde el service worker
 * o desde la caché HTTP (memoria: `project_panel_no_hay_cache_que_borrar`, 12-sep). Se dan de baja los
 * service workers, se vacía CacheStorage, y recién ahí se recarga con un parámetro que rompe la caché.
 * IndexedDB (la caché persistente de datos) se queda: ya se invalida sola por versión.
 */

export interface VersionPublicada { version: string; builtAt?: string }

/** La versión que corre en esta pestaña: el sha del build, o «dev» fuera de un deploy. */
export function versionQueCorre(): string {
  return (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev';
}

/**
 * Puro: ¿la publicada es otra? En desarrollo (`dev`) nunca: no hay deploy con el que comparar. Y una
 * respuesta vacía o rara tampoco cuenta: un `version.json` roto no puede bloquear el panel.
 */
export function hayVersionNueva(corre: string, publicada: VersionPublicada | null | undefined): boolean {
  if (!publicada || typeof publicada.version !== 'string') return false;
  const p = publicada.version.trim();
  if (!p || corre === 'dev' || !corre) return false;
  return p !== corre;
}

/** Cada cuánto se pregunta. Cinco minutos: un deploy tarda eso en importar, y no es tráfico que se note. */
export const CADA_MS = 5 * 60 * 1000;

export async function traerLaVersionPublicada(f: typeof fetch = fetch): Promise<VersionPublicada | null> {
  try {
    const r = await f(`/version.json?t=${Date.now()}`, { cache: 'no-store', headers: { 'Cache-Control': 'no-cache' } });
    if (!r.ok) return null;
    const j = (await r.json()) as Partial<VersionPublicada> | null;
    return j && typeof j.version === 'string' ? { version: j.version, builtAt: j.builtAt } : null;
  } catch {
    return null;
  }
}

/** Da de baja los service workers, vacía las cachés y recarga sin que nada viejo pueda volver a servirse. */
export async function actualizarAhora(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registros = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registros.map((r) => r.unregister().catch(() => false)));
    }
  } catch { /* sin SW no hay nada que dar de baja */ }
  try {
    if ('caches' in window) {
      const claves = await caches.keys();
      await Promise.all(claves.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch { /* la caché HTTP la rompe el parámetro de abajo */ }
  const url = new URL(window.location.href);
  url.searchParams.set('v', String(Date.now()));
  window.location.replace(url.toString());
}
